<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { AkParagraph, AkIcon } from '@ankorstore/design-system';
import { useIntersectionObserver } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { getGlobalConfig } from '@monolith/legacy/services/global-config';
import { formatPrice } from '@core/utilities/price.i18n';
import Analytics from '@monolith/legacy/services/analytics';
import {UserClick, UserDisplayEvent} from '@ankorstore/events-tracking'
import { PromocodeFirstCheckout } from '@ankorstore/api-internal/PromocodeFirstCheckout';
import { captureException } from '@core/plugins/sentry';
import { isEnabled, FeatureFlag } from '@monolith/legacy/services/features';

defineOptions({ name: 'WelcomeOfferBanner' });

const store = useStore();
const route = useRoute();
const { t } = useI18n();

const bannerRef = ref<HTMLElement | null>(null);
const hasTrackedView = ref(false);
const promocodeLabel = ref<string | null>(null);
const isCopied = ref(false);

const retailer = computed(() => store.getters['retailer']);

const hasActiveWelcomeOffer = computed(() => {
  return retailer.value?.new_welcome_promocode_active === true;
});

const isPayment = computed(() => route.path.startsWith('/checkout/payment'));

// getGlobalConfig() can return null when the global-config meta tag is gated
// off (fc-1327-meta-global-config) and the API fallback hasn't populated the
// cache yet — e.g. unauthenticated visitors landing on /lift/ pages.
const globalConfig = getGlobalConfig();

const isReady = computed(() => {
  return !!globalConfig && isEnabled(FeatureFlag.WelcomeOfferBanner) && hasActiveWelcomeOffer.value && !!promocodeLabel.value && !isPayment.value;
});

const BRAND_COUNT = globalConfig?.acquisition_offer_max_brands ?? 0;
const amountPerBrand = computed(() =>
  globalConfig ? formatPrice(globalConfig.acquisition_offer_amount, '$0') : '',
);

const totalAmount = computed(() => {
  const perBrand = globalConfig?.acquisition_offer_amount;
  if (!perBrand) {
    return '';
  }
  const total = { ...perBrand, amount: perBrand.amount * BRAND_COUNT };
  return formatPrice(total, '$0');
});

async function fetchPromocodeLabel() {
  try {
    const client = new PromocodeFirstCheckout();
    const response = await client.displayDiscoveryBrandAndPromocodeForFirstCheckout();
    promocodeLabel.value = response.meta?.promocode?.action_label ?? null;
  } catch (error) {
    if (error instanceof Error) {
      captureException(error);
    }
  }
}

function copyCode() {
  if (!promocodeLabel.value) {
    return;
  }

  navigator.clipboard.writeText(promocodeLabel.value);
  isCopied.value = true;

  setTimeout(() => {
    isCopied.value = false;
  }, 3000);

  Analytics.track(
    UserClick({
      component: 'welcome_offer_banner',
      action: 'copy_promocode',
      promocode: promocodeLabel.value,
    }),
  );
}

function trackBannerViewed() {
  if (hasTrackedView.value) {
    return;
  }
  hasTrackedView.value = true;
  Analytics.track(
    UserDisplayEvent({
      component: 'welcome_offer_banner',
      value_proposition: 'welcome_offer_banner',
      url_path: route.path
    }),
  );
}

onMounted(() => {
  if (hasActiveWelcomeOffer.value) {
    fetchPromocodeLabel();
  }
});

watch(
  [isReady, bannerRef],
  ([ready, el]) => {
    if (ready && el) {
      useIntersectionObserver(
        bannerRef,
        ([entry]) => {
          if (entry.isIntersecting) {
            trackBannerViewed();
          }
        },
        { threshold: 0.5 },
      );
    }
  },
  { once: true, flush: 'post' },
);
</script>

<template>
  <div
    v-if="isReady"
    ref="bannerRef"
    class="welcome-offer-banner"
    data-testid="welcomeOfferBanner"
  >
    <div class="welcome-offer-banner__content">
      <AkParagraph
        size="sm"
        class="welcome-offer-banner__text"
        data-testid="welcomeOfferBannerText"
      >
        {{ t('welcomeOffer.banner.stickyMessage', { totalAmount, amountPerBrand, brandCount: BRAND_COUNT, code: promocodeLabel }) }}
      </AkParagraph>
      <button
        class="welcome-offer-banner__code"
        data-testid="welcomeOfferBannerCopyCode"
        @click="copyCode"
      >
        <span class="welcome-offer-banner__code-label">{{ promocodeLabel }}</span>
        <AkIcon
          :symbol="isCopied ? 'check' : 'copy-keyboard'"
          size="sm"
          class="welcome-offer-banner__code-icon"
        />
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.welcome-offer-banner {
  @apply ds-w-screen -ds-mb-1;
  z-index:1;
  position: relative;
  left: 50%;
  transform: translateX(-50%);

  &__content {
    @apply ds-flex ds-items-center ds-justify-center ds-gap-2 ds-py-2 ds-px-4 ds-w-full ds-bg-discount-700 ds-text-white ds-text-center ds-text-lg;
  }

  &__text {
    @apply ds-text-white;
  }

  &__code {
    @apply ds-inline-flex ds-items-center ds-gap-1 ds-px-2 ds-py-1 ds-font-bold ds-text-sm ds-text-discount-700 ds-bg-success-500 ds-cursor-pointer ds-border-0;
  }

  &__code-icon {
    @apply ds-text-discount-700;
  }
}
</style>
